import axios from 'axios';
import JwtManager from '../utils/jwtManager';
import eHttpResponse from '../assets/enums/eHttpResponse';
import eNotificationType from '../assets/enums/eNotificationType';
import { toast } from 'react-toastify';
import { showLoader, hideLoader } from '../utils/LoaderContext'; // Ensure the correct path
import reduxManager from '../utils/ReduxManager';
import { invalidateUser } from '../store/stores/user/user.store';
import { invalidateMenu } from '../store/stores/user/menu.store';
import { invalidatePermissions } from '../store/stores/user/permission.store';
import { navigateTo } from '../store/stores/navigation/navigation.store';
import { resolvePageRedirectRoute } from '../constants/pageRedirect';

let isHandlingUnauthorized = false;

const isBackgroundRequest = (url?: string) => {
  if (!url) return false;

  return (
    url.includes('/api/print/job/payload') ||
    url.includes('/api/print/done') ||
    url.includes('/api/print/fail')
  );
};

const handleResponseMessage = (
  message: string,
  notificationType: eNotificationType
) => {
  switch (notificationType) {
    case eNotificationType.Success:
      toast.info(message, {
        autoClose: 1000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: true,
        progress: 0,
      });
      break;
    case eNotificationType.Error:
      toast.error(message, {
        autoClose: 1000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: true,
        progress: 0,
      });
      break;
    default:
      throw new Error('handleResponseMessage: Notification Type not handled');
  }
};

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

const handleUnauthorized = () => {
  if (isHandlingUnauthorized) return;
  isHandlingUnauthorized = true;

  const currentUser = reduxManager.store?.getState?.()?.user;
  const redirectPath = currentUser ? resolvePageRedirectRoute((currentUser as any)?.pageRedirect) : '/login';

  JwtManager.clearToken();
  window.sessionStorage.removeItem('companyTimeZone');

  const dispatch = reduxManager.dispatch;
  if (dispatch) {
    if (redirectPath === '/login') {
      dispatch(invalidateUser());
      dispatch(invalidateMenu());
      dispatch(invalidatePermissions());
    }

    dispatch(navigateTo(redirectPath));
  }

  setTimeout(() => {
    isHandlingUnauthorized = false;
  }, 1500);
};

axiosInstance.interceptors.request.use(
  (request) => {
    if (isBackgroundRequest(request.url)) {
      (request as any).skipLoader = true;
    }

    if (!(request as any).skipLoader) {
      showLoader();
    }
    const jwt = JwtManager.accessToken;
    if (jwt) {
      request.headers['Authorization'] = `Bearer ${jwt}`;
    }
    return request;
  },
  (error) => {
    if (!(error?.config as any)?.skipLoader) {
      hideLoader();
    }
    if (!(error?.config as any)?.skipErrorToast) {
      handleResponseMessage('Request error. Please try again.', eNotificationType.Error);
    }
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  (response) => {
    if (!(response.config as any).skipLoader) {
      hideLoader();
    }
    return response;
  },
  (error) => {
    const skipLoader = !!(error?.config as any)?.skipLoader;
    const skipErrorToast = !!(error?.config as any)?.skipErrorToast;
    if (!skipLoader) {
      hideLoader();
    }

    if (error.response) {
      if (error.response.status === eHttpResponse.Unauthorized) {
        if (JwtManager.accessToken) {
          handleUnauthorized();
        }
      } else if (!skipErrorToast && (error.response.data?.message || error.response.data?.Message || error.response.data?.error)) {
        handleResponseMessage(
          error.response.data?.message || error.response.data?.Message || error.response.data?.error,
          eNotificationType.Error
        );
      } else if (!skipErrorToast && error.response.status === eHttpResponse.NotFound) {
        handleResponseMessage('Requested resource not found', eNotificationType.Error);
      } else if (!skipErrorToast) {
        handleResponseMessage('An error occurred. Please try again.', eNotificationType.Error);
      }
    } else if (!skipErrorToast) {
      handleResponseMessage('Network error. Please check your connection.', eNotificationType.Error);
    }
    return Promise.reject(error);
  }
);

export default axiosInstance;
